import type { MutableValue, ObservableValue } from '@amazon/vinyl-observable'
import { data, externalData } from '@amazon/vinyl-observable'
import type { LogTarget, Unsubscribe } from '@amazon/vinyl-util'
import { logDebug, logError, logInfo, LruCache, requestWithRetry } from '@amazon/vinyl-util'
import type { MessageValue } from './format.js'

// ---------------------------------------------------------------------------
// Current locale
// ---------------------------------------------------------------------------

const target: LogTarget = {
    logPrefix: 'i18n',
}

const currentLocale: MutableValue<string> = data('und')

export function setLocale(locale: string): void {
    logInfo(target, 'locale changed to:', locale)
    currentLocale.value = locale
}

export function getLocale(): ObservableValue<string> {
    return currentLocale
}

export function resolveLocale(locale: string): string {
    return locale === 'und' ? i18nConfig.sourceLocale : locale
}

export function getBrowserLocale(): string {
    return typeof navigator !== 'undefined' ? navigator.language : 'und'
}

const browserLocale = externalData(getBrowserLocale(), (setData) => {
    if (typeof window === 'undefined') return () => {}
    const update = () => setData(getBrowserLocale())
    window.addEventListener('languagechange', update)
    return () => window.removeEventListener('languagechange', update)
})

export function bindBrowserLocale(): Unsubscribe {
    return browserLocale.onData(setLocale)
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Fetches the raw messages for one `{bundle, locale}`. The default resolves
 * `translationPath` over `fetch` (the browser path). A host with no HTTP
 * server for the translation files — e.g. a Node static-prerender running the
 * app against a mock DOM — can inject its own resolver (reading the JSON off
 * disk) via `setI18nConfig({ bundleResolver })`.
 */
export type BundleResolver = (
    bundle: string,
    locale: string
) => Promise<Record<string, MessageValue>>

/**
 * Global a host can set to inject a {@link BundleResolver} before the app
 * boots — used when the app is bundled (the host can't call `setI18nConfig`
 * on the app's own module instance). Read on every `loadBundle`.
 */
export const BUNDLE_RESOLVER_GLOBAL = '__bfI18nBundleResolver'

function readBootBundleResolver(): BundleResolver | undefined {
    const r = (globalThis as unknown as Record<string, unknown>)[BUNDLE_RESOLVER_GLOBAL]
    return typeof r === 'function' ? (r as BundleResolver) : undefined
}

export interface I18nConfig {
    readonly translationPath: string
    readonly sourceLocale: string
    /** Override the raw-bundle loader; defaults to a `fetch` of `translationPath`. */
    readonly bundleResolver?: BundleResolver
}

let i18nConfig: I18nConfig = {
    translationPath: './i18n/{bundle}/{locale}.json',
    sourceLocale: 'en-US',
}

export function setI18nConfig(partial: Partial<I18nConfig>): void {
    i18nConfig = { ...i18nConfig, ...partial }
    clearBundleCache()
}

export function getI18nConfig(): I18nConfig {
    return i18nConfig
}

// ---------------------------------------------------------------------------
// Bundle loading (cached, evicts on failure)
// ---------------------------------------------------------------------------

const cache = new LruCache<string, Promise<Record<string, MessageValue>>>(32)

export function loadBundle(bundle: string, locale: string): Promise<Record<string, MessageValue>> {
    const key = `${bundle}:${locale}`
    const cached = cache.get(key)
    if (cached) return cached

    // Injected resolver wins (e.g. a Node host reading bundles off disk);
    // otherwise fetch `translationPath` — the default browser path. The
    // resolver can be set on the config OR published on a boot global (the
    // latter lets a host that runs the BUNDLED app — e.g. the static
    // prerenderer — inject it before the app module evaluates, since it can't
    // reach this module's `setI18nConfig` on the app's own copy).
    const resolver = i18nConfig.bundleResolver ?? readBootBundleResolver()
    const promise = (
        resolver
            ? resolver(bundle, locale)
            : (() => {
                  const url = i18nConfig.translationPath
                      .replace('{locale}', locale)
                      .replace('{bundle}', bundle)
                  logDebug(target, `loadBundle, key=${key}, url=${url}`)
                  return requestWithRetry(url).then(
                      (r) => r.json() as Promise<Record<string, MessageValue>>
                  )
              })()
    ).catch((e) => {
        logError(target, `Could not load ${key}`, e)
        cache.delete(key)
        throw e
    })

    cache.set(key, promise)
    return promise
}

export function clearBundleCache(): void {
    cache.clear()
}
